import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/rbacApi";

// ===========================================
// FETCH ONLY ACTIVE MENUS FOR SIDEBAR
// ===========================================
export const fetchMenus = createAsyncThunk(
  "menu/fetchMenus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/admin/menu");
      return res.data.menus || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch menus");
    }
  }
);

// ===========================================
// FETCH MENUS FOR ACCORDION (TREE STRUCTURE)
// ===========================================
export const fetchAllMenus = createAsyncThunk(
  "menu/fetchAllMenus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/admin/menu/tree");
      return res.data.menus || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch menus");
    }
  }
);

// ===========================================
// FETCH MENUS FOR DROPDOWN (FLAT STRUCTURE)
// ===========================================
export const fetchDropdownMenus = createAsyncThunk(
  "menu/fetchDropdownMenus",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/admin/menu/dropdown");
      return res.data.menus || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch dropdown menus");
    }
  }
);

// =========================
// CREATE MENU
// =========================
export const createMenu = createAsyncThunk(
  "menu/createMenu",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      await api.post("/api/admin/menu", data);
      dispatch(fetchAllMenus());
      dispatch(fetchDropdownMenus());
      dispatch(fetchMenus());
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =========================
// UPDATE MENU
// =========================
export const updateMenu = createAsyncThunk(
  "menu/updateMenu",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/api/admin/menu/${data.intMenuId}`, data);
      dispatch(fetchAllMenus());
      dispatch(fetchDropdownMenus());
      dispatch(fetchMenus());
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =========================
// SOFT DELETE (DISABLE)
// =========================
export const deleteMenu = createAsyncThunk(
  "menu/deleteMenu",
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/api/admin/menu/${id}`);
      dispatch(fetchAllMenus());
      dispatch(fetchDropdownMenus());
      dispatch(fetchMenus());
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =========================
// TOGGLE ACTIVE/INACTIVE
// =========================
export const toggleMenuStatus = createAsyncThunk(
  "menu/toggleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/admin/menu/toggle/${id}`);
      return { id };
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// =========================
// SLICE
// =========================
const menuSlice = createSlice({
  name: "menu",
  initialState: {
    sidebarMenus: [],
    dropdownMenus: [],
    adminMenus: [],

    loading: false,
    actionLoading: false,
    error: null,
    success: null,
  },

  reducers: {
    clearMenus: (state) => {
      state.menus = [];
    },
    clearMenuStatus: (state) => {
      state.error = null;
      state.success = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // FETCH ONLY ACTIVE MENUS FOR SIDEBAR
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.sidebarMenus = action.payload;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FETCH MENUS FOR ACCORDION
      .addCase(fetchAllMenus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.adminMenus = action.payload;
      })
      .addCase(fetchAllMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FETCH MENUS FOR DROPDOWN
      .addCase(fetchDropdownMenus.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDropdownMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownMenus = action.payload;
      })
      .addCase(fetchDropdownMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // CREATE MENU
      .addCase(createMenu.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createMenu.fulfilled, (state) => {
        state.actionLoading = false;
        state.success = "Created";
      })
      .addCase(createMenu.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // UPDATE MENU
      .addCase(updateMenu.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateMenu.fulfilled, (state) => {
        state.actionLoading = false;
        state.success = "Updated";
      })
      .addCase(updateMenu.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // DELETE MENU (SOFT DELETE / DISABLE)
      .addCase(deleteMenu.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteMenu.fulfilled, (state) => {
        state.actionLoading = false;
        state.success = "Disabled";
      })
      .addCase(deleteMenu.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // TOGGLE ACTIVE/INACTIVE
      .addCase(toggleMenuStatus.pending, (state) => {
        state.actionLoading = true;
      })

      .addCase(toggleMenuStatus.fulfilled, (state, action) => {
        state.actionLoading = false;

        const targetId = action.payload.id;

        const toggleRecursive = (menus) => {
          return menus.map((menu) => {
            const updated = {
              ...menu,
              children: menu.children
                ? toggleRecursive(menu.children)
                : [],
            };

            if (menu.intMenuId === targetId) {
              updated.bitStatus = Number(menu.bitStatus) === 1 ? 0 : 1;
            }

            return updated;
          });
        };

        state.sidebarMenus = toggleRecursive(state.adminMenus)
          .filter((m) => Number(m.bitStatus) === 1);
      })

      .addCase(toggleMenuStatus.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });


  },
});

export const { clearMenus, clearMenuStatus } = menuSlice.actions;
export default menuSlice.reducer;